/* eslint-disable no-useless-escape */
/* eslint-disable no-restricted-globals */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useRef } from "react";
import slugify from "slugify";
import { PulseLoader } from "react-spinners";
import { APIContext } from "../../api/api";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import HelmetLayout from "../../components/HelmetLayout";
import Creatable from "react-select/creatable";
import {
  ArrowLeft,
  Trash2,
  Save,
  Upload,
  Image as ImageIcon,
  Layers,
} from "lucide-react";

export default function EditSection({ isNew }) {
  const navigate = useNavigate();
  const { sectionID } = useParams();
  const { getSection, deleteSection, createNewSection, updateSection } =
    useContext(APIContext);

  const [loading, setLoading] = useState(!isNew);
  const [sectionName, setSectionName] = useState("");
  const [sectionCategory, setSectionCategory] = useState("");
  const [sectionDescription, setSectionDescription] = useState("");
  const [sectionJSON, setSectionJSON] = useState("");
  const [sectionThumbnail, setSectionThumbnail] = useState("");
  const [sectionThumbnailFile, setSectionThumbnailFile] = useState(null);
  const [sectionMeta, setSectionMeta] = useState({});
  const [isSectionDeleting, setIsSectionDeleting] = useState(false);
  const [isSectionUpdating, setIsSectionUpdating] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const thumbnailRef = useRef(null);

  useEffect(() => {
    if (!isNew) {
      getSection(sectionID)
        .then((sectiondata) => {
          if (sectiondata) {
            setSectionName(sectiondata.section_name || "");
            setSectionDescription(sectiondata.section_description || "");
            setSectionCategory(sectiondata.section_category || "");
            setSectionJSON(
              typeof sectiondata.section_json === "string"
                ? sectiondata.section_json
                : JSON.stringify(sectiondata.section_json, null, 2)
            );
            setSectionThumbnail(sectiondata.section_thumbnail || "");
            setSectionMeta(sectiondata.section_meta || {});
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error("Failed to load section data");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isNew, sectionID]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (isSectionUpdating) return;

    if (!sectionName.trim()) {
      toast.error("Section name is required.");
      return;
    }

    setIsSectionUpdating(true);
    try {
      if (isNew) {
        await createNewSection(
          sectionName,
          sectionDescription,
          sectionThumbnailFile,
          sectionCategory,
          sectionMeta,
          sectionJSON
        );
        toast.success("Section created successfully!");
        navigate("/sections");
      } else {
        await updateSection(
          sectionID,
          sectionName,
          sectionDescription,
          sectionThumbnailFile,
          sectionCategory,
          sectionMeta,
          sectionJSON
        );
        toast.success("Section updated successfully!");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to save section");
    } finally {
      setIsSectionUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (isSectionDeleting) return;
    setIsSectionDeleting(true);
    try {
      await deleteSection(sectionID);
      toast.success("Section deleted successfully!");
      navigate("/sections");
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to delete section");
      setIsSectionDeleting(false);
    }
  };

  return (
    <>
      <HelmetLayout
        title={isNew ? "New Section" : `${sectionName || "Edit"} - Section`}
      />

      <div className="space-y-6 pb-16 max-w-4xl">
        {/* Navigation & Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <Link target="_blank" rel="noopener noreferrer"
              to="/sections"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors mb-2"
            >
              <ArrowLeft size={14} />
              Back to Sections
            </Link>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Layers className="text-[#623ce7]" size={24} />
              {isNew ? "Create New Section" : `Edit Section: ${sectionName}`}
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              {isNew
                ? "Configure metadata, category, and JSON component structure for a new section."
                : `Update metadata, visual assets, and JSON content for section ${sectionID}.`}
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            {!isNew && (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                className="px-3.5 py-2 text-xs font-semibold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={isSectionUpdating}
              className="px-4 py-2 text-xs font-semibold text-white bg-[#623ce7] hover:bg-[#5030bc] rounded-lg transition-colors shadow-sm flex items-center gap-1.5 disabled:opacity-50"
            >
              {isSectionUpdating ? (
                <PulseLoader size={6} color="#ffffff" />
              ) : (
                <>
                  <Save size={14} />
                  {isNew ? "Create Section" : "Save Changes"}
                </>
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-16 text-center shadow-sm">
            <PulseLoader size={10} color="#623ce7" />
            <p className="text-xs text-slate-400 mt-3">Loading section...</p>
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-6">
            {/* General Info Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5">
              <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
                General Information
              </h2>

              {/* Name */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Section Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Hero Section with Gradient CTA"
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Category
                </label>
                <select
                  value={sectionCategory}
                  onChange={(e) => setSectionCategory(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all capitalize"
                >
                  <option value="">Select a category...</option>
                  <option value="hero">Hero</option>
                  <option value="features">Features</option>
                  <option value="cta">CTA</option>
                  <option value="testimonial">Testimonial</option>
                  <option value="team">Team</option>
                  <option value="pricing">Pricing</option>
                  <option value="services">Services</option>
                  <option value="counter">Counter</option>
                  <option value="contact-us">Contact Us</option>
                  <option value="about-us">About Us</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="location">Location</option>
                  <option value="coming-soon">Coming Soon</option>
                  <option value="cms-list">CMS List</option>
                  <option value="e-commerce">E-commerce</option>
                  <option value="footer">Footer</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Description
                </label>
                <textarea
                  rows={3}
                  placeholder="Detailed description of the section layout and components..."
                  value={sectionDescription}
                  onChange={(e) => setSectionDescription(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all resize-y"
                />
              </div>

              {/* Keywords */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Keywords & Tags
                </label>
                <Creatable
                  isMulti
                  placeholder="Type keyword and press Enter..."
                  options={[]}
                  value={sectionMeta?.keyword || []}
                  onChange={(selectedOptions) => {
                    setSectionMeta({
                      ...(sectionMeta || {}),
                      keyword: selectedOptions,
                    });
                  }}
                  onCreateOption={(inputValue) => {
                    const slug = slugify(inputValue, {
                      trim: false,
                      strict: true,
                      lower: true,
                    });
                    const newOption = { value: slug, label: inputValue };
                    setSectionMeta({
                      ...(sectionMeta || {}),
                      keyword: [...(sectionMeta?.keyword || []), newOption],
                    });
                  }}
                  className="text-sm"
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "#f8fafc",
                      borderColor: "#e2e8f0",
                      borderRadius: "0.5rem",
                      padding: "2px",
                      boxShadow: "none",
                      "&:hover": {
                        borderColor: "#cbd5e1",
                      },
                    }),
                  }}
                />
              </div>
            </div>

            {/* Thumbnail Asset Card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
              <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">
                Visual Assets
              </h2>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Section Thumbnail
                </label>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  {sectionThumbnail ? (
                    <div className="relative w-48 h-32 rounded-lg border border-slate-200 overflow-hidden bg-slate-100 shrink-0 shadow-sm">
                      <img
                        src={sectionThumbnail}
                        alt="Section thumbnail preview"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : (
                    <div className="w-48 h-32 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-slate-400 shrink-0">
                      <ImageIcon size={28} className="mb-1 text-slate-300" />
                      <span className="text-xs">No thumbnail</span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => thumbnailRef.current?.click()}
                      className="px-3.5 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors shadow-sm flex items-center gap-1.5"
                    >
                      <Upload size={14} />
                      {sectionThumbnail ? "Replace Image" : "Upload Thumbnail"}
                    </button>
                    <p className="text-[11px] text-slate-400">
                      PNG, JPG, or WebP recommended. Aspect ratio approx 16:9.
                    </p>
                  </div>

                  <input
                    ref={thumbnailRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onloadend = () => {
                          setSectionThumbnail(reader.result);
                        };
                        setSectionThumbnailFile(file);
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Section JSON Data */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <h2 className="text-base font-bold text-slate-900">
                    Section JSON Schema
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Component tree, classes, and responsive styles for this section.
                  </p>
                </div>
              </div>

              <textarea
                rows={12}
                value={sectionJSON}
                onChange={(e) => setSectionJSON(e.target.value)}
                placeholder="Paste section component JSON here..."
                className="w-full font-mono text-xs px-4 py-3 bg-slate-900 text-slate-100 rounded-lg border border-slate-800 focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all resize-y"
              />
            </div>

            {/* Bottom Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <Link target="_blank" rel="noopener noreferrer"
                to="/sections"
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 transition-colors"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={isSectionUpdating}
                className="px-5 py-2.5 text-xs font-semibold text-white bg-[#623ce7] hover:bg-[#5030bc] rounded-lg transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50"
              >
                {isSectionUpdating ? (
                  <PulseLoader size={6} color="#ffffff" />
                ) : (
                  <>
                    <Save size={14} />
                    {isNew ? "Create Section" : "Save Changes"}
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Custom Confirmation Modal for Deletion */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white rounded-xl border border-slate-200 shadow-2xl max-w-md w-full p-6 space-y-4">
              <h3 className="text-lg font-bold text-slate-900">
                Delete Section
              </h3>
              <p className="text-sm text-slate-600">
                Are you sure you want to permanently delete{" "}
                <strong className="text-slate-900 font-semibold">
                  {sectionName || sectionID}
                </strong>
                ? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  disabled={isSectionDeleting}
                  className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isSectionDeleting}
                  className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg transition-colors flex items-center gap-2 shadow-sm disabled:opacity-50"
                >
                  {isSectionDeleting ? (
                    <PulseLoader size={6} color="#ffffff" />
                  ) : (
                    <>
                      <Trash2 size={14} />
                      Confirm Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
